import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@cloudscape-design/components/table";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Badge from "@cloudscape-design/components/badge";
import { getDecisionLog } from "../api";
import type { DecisionLogEntry } from "../types";

const ACTION_TYPE_COLORS: Record<string, string> = {
  COMMENT_POSTED: "blue",
  TICKET_CLOSED: "green",
  NOTE_ADDED: "grey",
  WAITING_ON_CHANGED: "grey",
  STAGE_CHANGED: "grey",
  REASSIGNED: "grey",
  SEVERITY_CHANGED: "red",
};

export default function DecisionLogPage() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [ticketFilter, setTicketFilter] = useState("");

  const localEntries = getDecisionLog({
    ticketId: ticketFilter || undefined,
    keyword: searchText || undefined,
  });

  let allEntries = [...localEntries];

  // Apply filters
  if (ticketFilter) {
    allEntries = allEntries.filter((e) => e.ticketId.includes(ticketFilter));
  }
  if (searchText) {
    const kw = searchText.toLowerCase();
    allEntries = allEntries.filter(
      (e) =>
        e.details.toLowerCase().includes(kw) ||
        e.actionType.toLowerCase().includes(kw) ||
        e.actor.toLowerCase().includes(kw)
    );
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1" counter={`(${allEntries.length})`}>
        Decision Log
      </Header>

      <SpaceBetween direction="horizontal" size="s">
        <Input
          value={searchText}
          onChange={({ detail }) => setSearchText(detail.value)}
          placeholder="Search by keyword..."
          type="search"
        />
        <Input
          value={ticketFilter}
          onChange={({ detail }) => setTicketFilter(detail.value)}
          placeholder="Filter by ticket ID..."
        />
        <Button
          onClick={() => {
            setSearchText("");
            setTicketFilter("");
          }}
        >
          Clear
        </Button>
      </SpaceBetween>

      <Table
        columnDefinitions={[
          {
            id: "timestamp",
            header: "Time",
            cell: (item: DecisionLogEntry) =>
              new Date(item.timestamp).toLocaleString(),
            width: 180,
          },
          {
            id: "ticketId",
            header: "Ticket",
            cell: (item: DecisionLogEntry) => (
              <Button
                variant="link"
                onClick={() => navigate(`/tickets/${item.ticketId}`)}
              >
                {item.ticketId}
              </Button>
            ),
            width: 130,
          },
          {
            id: "actor",
            header: "Actor",
            cell: (item: DecisionLogEntry) => item.actor,
            width: 100,
          },
          {
            id: "actionType",
            header: "Action",
            cell: (item: DecisionLogEntry) => (
              <Badge
                color={
                  (ACTION_TYPE_COLORS[item.actionType] as "blue" | "green" | "grey" | "red") ||
                  "grey"
                }
              >
                {item.actionType.replace(/_/g, " ")}
              </Badge>
            ),
            width: 160,
          },
          {
            id: "details",
            header: "Details",
            cell: (item: DecisionLogEntry) => (
              <Box>{item.details.substring(0, 150)}{item.details.length > 150 ? "..." : ""}</Box>
            ),
          },
        ]}
        items={allEntries}
        variant="full-page"
        stickyHeader
        empty={
          <Box textAlign="center" color="inherit">
            <b>No entries</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No decision log entries found. Actions you take in the dashboard will appear here.
            </Box>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
